import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, ShieldCheck, LogOut, AlertCircle } from 'lucide-react';
import styles from './Pending.module.css';
import { useAuthStore } from '../../store/appStore';
import { authApi } from '../../lib/apiClient';
import { UserRole } from '../../enums/userRole';

interface ApprovalStatus {
  isPending: boolean;
  role?: string;
  message: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export default function Pending() {
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const logout = useAuthStore(state => state.logout);
  const fetchCurrentUser = useAuthStore(state => state.fetchCurrentUser);
  const [status, setStatus] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isBlocked = location.state?.blocked === true || user?.role?.id === UserRole.Blocked;
  const blockedMessage = location.state?.message || 'Your account has been blocked due to too many failed login attempts. Please contact an administrator for assistance.';

  // If authenticated and not pending/blocked anymore, send to dashboard
  useEffect(() => {
    if (isAuthenticated && user && user.role?.id !== UserRole.Pending && user.role?.id !== UserRole.Blocked) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // For authenticated pending users, poll approval status
  useEffect(() => {
    // If blocked, don't poll - show blocked message
    if (isBlocked) {
      setStatus({
        isPending: false,
        message: blockedMessage,
      });
      return;
    }

    if (!isAuthenticated) {
      // For newly registered (not logged in) users, show a static message
      setStatus({
        isPending: true,
        message:
          'Your account has been created and is awaiting approval. Once an administrator approves your account, you will be able to sign in.',
      });
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setLoading(true);
        const result = await authApi.getApprovalStatus();
        if (cancelled) return;
        setStatus(result);

        if (!result.isPending && result.role !== 'Blocked') {
          // User was approved - refresh store so ProtectedRoute sees the new role
          await fetchCurrentUser();
          navigate('/dashboard', { replace: true });
        }
      } catch {
        // If approval-status fails (e.g. token expired), send to login
        if (!cancelled) {
          navigate('/login', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStatus();
    const intervalId = setInterval(fetchStatus, 25000); // poll every 25s

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAuthenticated, navigate, isBlocked, blockedMessage, fetchCurrentUser]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const email = status?.email || user?.email;
  const firstName = status?.firstName || user?.first_name;
  const lastName = status?.lastName || user?.last_name;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.badge}>
            {isBlocked ? <AlertCircle size={14} /> : <Clock size={14} />}
            {isBlocked ? 'Account blocked' : 'Pending approval'}
          </div>
          <h1 className={styles.title}>
            {isBlocked ? 'Your account has been blocked' : 'Your account is awaiting approval'}
          </h1>
          {(email || firstName || lastName) && (
            <p className={styles.userInfo}>
              {firstName || lastName ? (
                <>
                  <span className={styles.highlight}>
                    {firstName} {lastName}
                  </span>
                  {email && <> · {email}</>}
                </>
              ) : (
                <span className={styles.highlight}>{email}</span>
              )}
            </p>
          )}
        </div>

        <div className={styles.body}>
          <p>{status?.message || 'Your account is pending approval. This usually takes a short time.'}</p>
          <ul className={styles.list}>
            <li>
              <ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Approval ensures only authorized users can access organization tools.
            </li>
            <li>You’ll be able to sign in as soon as an administrator approves your account.</li>
          </ul>
          <div className={styles.statusPill}>
            <Clock size={14} />
            {loading ? 'Checking approval status…' : 'We’ll periodically check if your account has been approved.'}
          </div>
          <p className={styles.smallMuted}>
            If this is taking longer than expected, please contact your administrator.
          </p>
        </div>

        <div className={styles.actions}>
          {isBlocked ? (
            <button 
              type="button" 
              className={styles.primaryButton} 
              onClick={() => navigate('/login')}
              disabled
            >
              Sign in disabled
            </button>
          ) : !isAuthenticated ? (
            <button type="button" className={styles.primaryButton} onClick={() => navigate('/login')}>
              Back to sign in
            </button>
          ) : (
            <button type="button" className={styles.primaryButton} onClick={handleLogout}>
              <LogOut size={14} />
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

